import { Edit } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { getDocumentsAvailablesForFactoring } from 'lib/api';
import { getDocuments } from 'lib/api/document';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ClientInfo from '../../../documentsSelector/components/ClientInfo';
import CreditNoteAlert from '../../../documentsSelector/components/CreditNoteAlert';
import EditDocumentManually from '../../../documentsSelector/components/editDocument';
import type {
  DocumentWithMetadata,
  EnrolledType,
} from '../../../documentsSelector/types';
import { buildDocumentsPayload } from '../../../documentsSelector/utils/queryBuilder';

interface EditorProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRequest: any;
  searched: boolean;
  setSearched: (searched: boolean) => void;
  subProductSelected: any;
  businessIdentifier: string;
  subProduct: any;
  selectedDocuments: any;
  setSelectedDocuments: (selectedDocuments: any) => void;
  setOpenShoppingCart: (openShoppingCart: boolean) => void;
}

const Editor = (props: EditorProps) => {
  const {
    open,
    setOpen,
    selectedRequest,
    subProductSelected,
    businessIdentifier,
    selectedDocuments,
    setSelectedDocuments,
    setOpenShoppingCart,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [documents, setDocuments] = React.useState<any>([]);
  const [searchedBySubProduct, setSearchedBySubProduct] = React.useState(false);
  const [isEnrolled, setIsEnrolled] = React.useState<boolean | undefined>(
    undefined,
  );
  const [selectedIds, setSelectedIds] = React.useState<any>([]);
  const [configuration, setConfiguration] = useState<any>(null);
  const [openEditDocument, setOpenEditDocument] = useState<boolean>(false);
  const [documentToEdit, setDocumentToEdit] =
    useState<DocumentWithMetadata | null>(null);

  // Filters
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const requestId = '';
  const [enrolledType, setEnrolledType] = useState<EnrolledType>(null);
  const [folio, setFolio] = useState('');
  const [cachedDocuments, setCachedDocuments] = useState<any>([]);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const fatherDocumentIds = Array.from(
    new Set(
      selectedRequest.documents
        .map((document: any) => document.fatherDocumentId)
        .filter((id: string | null | undefined): id is string => id != null),
    ),
  );

  const { data: fatherDocumentsData, isLoading: isLoadingFatherDocuments } =
    getDocuments(dispatch, fatherDocumentIds as string[]);
  const fatherDocuments = fatherDocumentsData?.documents || [];

  const getSelectedDocuments = (documents: any[]) => {
    const selectedDocuments: any[] = [];
    const fatherDocumentsIds: any[] = [];
    for (const document of documents) {
      if (
        document.fatherDocumentId &&
        !fatherDocumentsIds.includes(document.fatherDocumentId)
      ) {
        fatherDocumentsIds.push(document.fatherDocumentId);
      } else if (!document.fatherDocumentId) {
        selectedDocuments.push({ ...document, isOld: true });
      }
    }
    for (const fatherDocumentId of fatherDocumentsIds) {
      const fatherDocument = fatherDocuments.find(
        (document: any) => document.id === fatherDocumentId,
      );
      console.log('fatherDocument', fatherDocument);
      const childDocuments = documents.filter(
        (document: any) => document.fatherDocumentId === fatherDocumentId,
      );
      selectedDocuments.push({
        ...fatherDocument,
        subDocuments: childDocuments.map((childDocument: any) => ({
          ...childDocument,
          isOld: true,
        })),
      });
    }
    return selectedDocuments.map((document: any) => ({
      ...document,
      subDocuments: document.subDocuments || [],
    }));
  };

  const generatePayload = ({
    page,
    pageSize,
    overrideEnrolledType,
  }: {
    page: number;
    pageSize: number;
    overrideEnrolledType?: string;
  }) => {
    return buildDocumentsPayload({
      page,
      pageSize,
      folio,
      businessIdentifier,
      stakeholderIdentifier,
      stakeholderName,
      startDate,
      endDate,
      requestId,
      enrolledType:
        (overrideEnrolledType as EnrolledType) ??
        (enrolledType as EnrolledType),
      selectedIds,
      isEnrolled,
    });
  };

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de confirmación',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];

  const handleSetSelected = (selectedIds: any) => {
    const newCachedDocuments = [...cachedDocuments];
    for (const id of selectedIds) {
      const document = documents.find((document: any) => document.id === id);
      if (document) {
        const newCachedIds = [
          ...newCachedDocuments.map((document: any) => document.id),
        ];
        if (!newCachedIds.includes(document.id)) {
          newCachedDocuments.push(document);
        }
      }
    }
    setCachedDocuments(newCachedDocuments);
    const uniqueIds = [...new Set(selectedIds)];
    setSelectedIds(uniqueIds);
  };
  const searchSuggestedDocuments = async ({
    page,
    pageSize,
    overrideEnrolledType,
  }: {
    page: number;
    pageSize: number;
    overrideEnrolledType?: string;
  }) => {
    const payload = generatePayload({
      page,
      pageSize,
      overrideEnrolledType,
    });
    const response = await getDocumentsAvailablesForFactoring(
      dispatch,
      businessIdentifier,
      subProductSelected,
      payload,
    );
    return response;
  };

  const handleSearchSuggestedDocuments = async ({
    page,
    pageSize,
    forceEnrolledType,
  }: {
    page: number;
    pageSize: number;
    forceEnrolledType?: string;
  }) => {
    try {
      // Actualizar enrolledType si viene forceEnrolledType
      if (forceEnrolledType) {
        setEnrolledType(forceEnrolledType as EnrolledType);
      }

      const response = await searchSuggestedDocuments({
        page,
        pageSize,
        overrideEnrolledType: forceEnrolledType,
      });

      if (!response) return;

      const allDocuments = [...cachedDocuments, ...response.data];
      const configuration = response.configuration;
      const documents = response.data;
      const pagination = response.pagination;
      const isEnrolled = response.isEnrolled;
      setConfiguration(configuration);
      setDocuments([...documents]);
      setCachedDocuments([...allDocuments]);
      setPagination(pagination);
      setIsEnrolled(isEnrolled);
      setSearchedBySubProduct(true);
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const cantSelectDocuments = (document: any) => {
    if (enrolledType === 'manual') {
      return false;
    }
    if (configuration.dontFilterOwnInvoices) {
      if (document.cessionaryIdentifier === configuration.companyIdentifier) {
        return false;
      }
    }
    if (configuration.showOnlyAvailables) {
      if (document.status === 'Transable') {
        return false;
      }
    }
    if (
      !configuration.dontFilterOwnInvoices &&
      !configuration.showOnlyAvailables
    ) {
      return false;
    }
    return true;
  };

  const handleSetPrefill = (documents: any) => {
    const selectedDocs = getSelectedDocuments(documents);
    let preSelectedIds = [];
    if (selectedDocuments.length) {
      preSelectedIds = selectedDocuments.map((document: any) => document.id);
      const newSelectedDocuments = selectedDocuments.map((document: any) => {
        if (document.subDocuments) {
          return {
            ...document,
            subDocuments: document.subDocuments.map((subDocument: any) => ({
              ...subDocument,
              paymentDate:
                documents.find((doc: any) => doc.id === subDocument.id)
                  ?.paymentDate ?? undefined,
              dueDate:
                documents.find((doc: any) => doc.id === subDocument.id)
                  ?.paymentDate ?? undefined,
            })),
          };
        }
        return document;
      });
      setCachedDocuments(newSelectedDocuments);
    } else {
      preSelectedIds = selectedDocs.map((document: any) => document.id);
      const newSelectedDocuments = selectedDocs.map((document: any) => {
        if (document.subDocuments) {
          return {
            ...document,
            subDocuments: document.subDocuments.map((subDocument: any) => ({
              ...subDocument,
              paymentDate:
                documents.find((doc: any) => doc.id === subDocument.id)
                  ?.paymentDate ?? undefined,
              dueDate:
                documents.find((doc: any) => doc.id === subDocument.id)
                  ?.paymentDate ?? undefined,
            })),
          };
        }
        return document;
      });
      setCachedDocuments(newSelectedDocuments);
    }
    const uniquePreSelectedIds = [...new Set(preSelectedIds)];
    setSelectedIds(uniquePreSelectedIds);
  };

  const triggerSearch = async () => {
    setLoading(true);
    try {
      await handleSearchSuggestedDocuments({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDocuments = async () => {
    let documentsSelected: any[] = cachedDocuments.filter((document: any) =>
      selectedIds.includes(document.id),
    );
    // Remove duplicates from documentsSelected based on the document id
    const uniqueDocumentsSelected = documentsSelected.filter(
      (document, index, self) =>
        index === self.findIndex((doc) => doc.id === document.id),
    );
    documentsSelected = uniqueDocumentsSelected;
    if (!documentsSelected) {
      return;
    }
    // Add paymentDate from selectedDocuments to documentsSelected in case it exists
    documentsSelected.forEach((document: any) => {
      const selectedDocument = selectedRequest.documents.find(
        (doc: any) => doc.id === document.id,
      );
      document.amountAvailable = document.amountAvailable
        ? document.amountAvailable
        : document.amount;
      if (selectedDocument) {
        if (selectedDocument.paymentDate) {
          document.dueDate = selectedDocument.paymentDate;
        }
        if (selectedDocument.subDocuments) {
          document.subDocuments = selectedDocument.subDocuments;
          document.subDocuments.forEach((subDocument: any) => {
            if (subDocument.paymentDate) {
              subDocument.dueDate = subDocument.paymentDate;
            }
          });
        }
      }
    });
    setSelectedDocuments(documentsSelected);
    setOpenShoppingCart(true);
    setOpen(false);
  };

  const setSelectedDocumentsInit = () => {
    handleSetPrefill(selectedRequest.documents);
  };

  const buildTableHeaders = () => {
    const baseHeaders = [
      { key: 'folio', label: 'Folio' },
      // Columna de código (si aplica)
      ...(selectedDocuments[0]?.code
        ? [{ key: 'code', label: 'Código Doc.' }]
        : []),
      // Alerta de notas de crédito (solo si es SII y no manual)
      ...(isEnrolled && enrolledType !== 'manual'
        ? [
            {
              key: 'creditNoteAmount',
              label: '',
              type: 'custom',
              customComponent: (value: any) => (
                <CreditNoteAlert creditNotesValue={Number(value) || 0} />
              ),
            },
          ]
        : []),
      // Columnas base siempre visibles
      { key: 'amount', label: 'Monto', type: 'money' },
      {
        key: 'amountAvailable',
        label: 'Monto disponible',
        type: 'money',
      },
      {
        key: 'stakeholderName',
        label: 'Nombre receptor',
      },
      {
        key: 'issuedDate',
        label: 'Fecha de emisión',
        type: 'date',
      },
      {
        key: 'receiptDate',
        label: 'Fecha de recepción',
        type: 'eight-days-date',
      },
      {
        key: 'confirmDate',
        label: 'Fecha de confirmación',
        type: 'date',
      },
      // Columna de cancelación (opcional)
      ...(enrolledType === 'sii' || enrolledType === 'manual'
        ? [{ key: 'cancelDate', label: 'Cancelación', type: 'date' }]
        : []),
      // Columna de NC (nota de crédito) - siempre visible
      {
        key: 'creditNoteAmount',
        label: 'NC',
        type: 'money',
      },
      // Columna de solicitud (opcional)
      ...(enrolledType === 'sii' || enrolledType === 'manual'
        ? [{ key: 'requestId', label: 'Solicitud' }]
        : []),
      // Columna de estado SII (solo si enrolledType === 'sii')
      ...(enrolledType === 'sii'
        ? [
            {
              key: 'status',
              label: 'Estado SII',
              type: 'cession',
            },
          ]
        : []),
      // Columna de editar (solo si enrolledType === 'manual')
      ...(enrolledType === 'manual'
        ? [
            {
              key: 'edit',
              label: 'Editar',
              type: 'custom',
              customComponent: (_value: unknown, row: DocumentWithMetadata) => {
                return (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDocumentToEdit(row);
                      setOpenEditDocument(true);
                    }}
                    sx={{ padding: '4px' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ];

    return baseHeaders;
  };

  const setup = async () => {
    setLoading(true);
    try {
      await handleSearchSuggestedDocuments({
        page: pagination.page,
        pageSize: pagination.pageSize,
        forceEnrolledType: 'manual',
      });
      setSelectedDocumentsInit();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoadingFatherDocuments) {
      void setup();
    }
  }, [isLoadingFatherDocuments]);

  return (
    <Dialog open={open} fullWidth maxWidth="xl">
      <DialogTitle>
        <HeaderWithActions title="Editar Documentos" type="dialogHeader" />
      </DialogTitle>

      <DialogContent>
        {loading ||
        isLoadingFatherDocuments ||
        !searchedBySubProduct ||
        isEnrolled === undefined ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div style={{ padding: '0px 0px 20px 0px' }}>
            {/* ClientInfo */}
            <ClientInfo
              identifier={businessIdentifier}
              businessName={
                selectedRequest?.businessName ||
                selectedRequest?.business?.businessName
              }
              isEnrolled={isEnrolled}
              id={businessIdentifier}
              onChangeClient={() => {
                // En modo edición, no permitimos cambiar el cliente
              }}
              editable={false}
            />

            <Divider />
            <Box sx={{ mb: 3, mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <ToggleButtonGroup
                  value={enrolledType || 'manual'}
                  color="primary"
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setEnrolledType(newValue);
                      setLoading(true);
                      void (async () => {
                        try {
                          await handleSearchSuggestedDocuments({
                            page: 1,
                            pageSize: PAGE_SIZE,
                            forceEnrolledType: newValue,
                          });
                        } finally {
                          setLoading(false);
                        }
                      })();
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      minWidth: '140px',
                    },
                  }}
                >
                  <ToggleButton value="sii" disabled={isEnrolled !== true}>
                    Desde SII
                  </ToggleButton>
                  <ToggleButton value="manual">
                    Creados manualmente
                  </ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', gap: '10px' }}>
                  <GlobalSearcher
                    filters={filters}
                    triggerSearch={triggerSearch}
                    dispatch={dispatch}
                    disableSearch={loading}
                  />
                </Box>
              </Box>
            </Box>
            <div>
              <TableWithPagination
                key={`table-${isEnrolled}-${enrolledType || 'all'}-${subProductSelected}`}
                dataHeaders={buildTableHeaders()}
                data={documents}
                selected={selectedIds}
                setSelected={handleSetSelected}
                searchData={searchSuggestedDocuments}
                setData={setDocuments}
                pagination={pagination}
                setPagination={setPagination}
                loading={loading}
                setLoading={setLoading}
                cantSelectRow={cantSelectDocuments}
              />
            </div>
          </div>
        )}
        <DialogActions>
          <CustomButton
            variant="contained"
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {' '}
            Salir
          </CustomButton>
          {searchedBySubProduct && (
            <CustomButton
              variant="contained"
              color="primary"
              disabled={!selectedIds.length}
              onClick={handleSelectDocuments}
            >
              {' '}
              Seleccionar {selectedIds.length > 0 && `(${selectedIds.length})`}
            </CustomButton>
          )}
        </DialogActions>
      </DialogContent>
      {openEditDocument && documentToEdit && (
        <EditDocumentManually
          business={{
            id: businessIdentifier,
            identifier: businessIdentifier,
            businessName:
              selectedRequest?.businessName ||
              selectedRequest?.business?.businessName,
          }}
          open={openEditDocument}
          setOpen={setOpenEditDocument}
          document={documentToEdit}
          onDocumentUpdated={(updatedDocument) => {
            // Actualizar documentos en la tabla
            setDocuments((prev: DocumentWithMetadata[]) =>
              prev.map((doc: DocumentWithMetadata) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            // Actualizar documentos en caché
            setCachedDocuments((prev: DocumentWithMetadata[]) =>
              prev.map((doc: DocumentWithMetadata) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            // Cerrar el diálogo de edición
            setOpenEditDocument(false);
            setDocumentToEdit(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default Editor;
